"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Calendar, Clock, User, ArrowRight, CircleCheck as CheckCircle2, TriangleAlert as AlertTriangle, Wrench, House as Home, ShieldCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function RelatedPostsConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[...Array(3)].map((_, i) => <Card key={i} className="animate-pulse overflow-hidden" data-index={i}>
            <div className="h-48 bg-muted/50" />
            <CardHeader>
              <div className="h-4 w-1/3 bg-muted/50 rounded mb-2" />
              <div className="h-6 w-3/4 bg-muted/50 rounded" />
            </CardHeader>
            <CardContent>
              <div className="h-4 w-full bg-muted/50 rounded mb-2" />
              <div className="h-4 w-2/3 bg-muted/50 rounded" />
            </CardContent>
          </Card>)}
      </div>;
  }
  if (error || posts.length === 0) {
    return null;
  }
  return <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
      {posts.slice(0, 3).map((post, i) => <Card key={post.id} className="flex flex-col overflow-hidden transition-shadow hover:shadow-md" data-index={i}>
          {post.featuredImage && <div className="relative h-48 w-full overflow-hidden">
              <img src={post.featuredImage} alt={post.title} className="object-cover w-full h-full transition-transform duration-300 hover:scale-105" loading="lazy" />
            </div>}
          <CardHeader>
            <div className="flex items-center gap-2 mb-2 text-xs text-muted-foreground">
              <SafeHydrate><Calendar className="size-3" /></SafeHydrate>
              <span>{post.date ? new Date(post.date).toLocaleDateString() : "Recent"}</span>
            </div>
            <CardTitle className="line-clamp-2 text-lg">
              <Link to={post.link || "#"} className="hover:text-primary transition-colors">
                {post.title}
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-grow">
            <CardDescription className="line-clamp-3">
              {post.excerpt ? post.excerpt.replace(/(<([^>]+)>)/gi, "") : "Read more about this topic in our detailed guide."}
            </CardDescription>
          </CardContent>
          <CardFooter>
            <Link to={post.link || "#"} className="w-full">
              <Button variant="outline" className="w-full group">
                Read Article
                <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </CardFooter>
        </Card>)}
    </div>;
}
export default function MaintenanceArticle() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="homemaintenancearticle" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
        
        {}
        <header className="mb-12 text-center md:text-left">
          <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mb-6">
            <Badge variant="secondary" className="bg-primary/10 text-primary hover:bg-primary/20">Home Ownership</Badge>
            <Badge variant="outline">Maintenance Guide</Badge>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold font-default tracking-tight mb-6 text-foreground">
            The Ultimate Guide to Home Maintenance: Protecting Your Biggest Investment
          </h2>
          
          <div className="flex flex-wrap items-center justify-center md:justify-start gap-6 text-sm text-muted-foreground mb-8">
            <div className="flex items-center gap-2">
              <User className="size-4" />
              <span>By Handy Heroes Editorial Team</span>
            </div>
            <div className="flex items-center gap-2">
              <SafeHydrate><Calendar className="size-4" /></SafeHydrate>
              <span>Updated October 24, 2023</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="size-4" />
              <span>8 min read</span>
            </div>
          </div>

          <div className="relative w-full overflow-hidden rounded-2xl shadow-lg mb-12">
            <AspectRatio ratio={16 / 9}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/home_maintenance_hero.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/home_maintenance_hero.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/home_maintenance_hero.webp-wvc-sizes" alt="A well-maintained suburban home exterior with a manicured lawn" className="object-cover w-full h-full" loading="eager" />
            </AspectRatio>
          </div>
        </header>

        {}
        <article className="prose prose-lg max-w-none prose-headings:font-default prose-headings:font-bold prose-a:text-primary hover:prose-a:text-primary/80 prose-img:rounded-xl">
          
          <p className="text-xl leading-relaxed text-muted-foreground mb-8">
            Owning a home is often described as the American Dream, but without proper care, that dream can quickly become a costly nightmare. Home maintenance isn't just about keeping your property looking good; it's a critical ongoing process that ensures safety, preserves value, and prevents minor issues from ballooning into major financial disasters.
          </p>

          <h2 className="text-3xl mt-12 mb-6 flex items-center gap-3">
            <ShieldCheck className="text-primary size-8" />
            The Tangible Benefits of Regular Home Maintenance
          </h2>
          
          <h3 className="text-2xl font-semibold mb-4 text-foreground">Why Regular Maintenance Matters</h3>

          <p className="mb-6">
            Many homeowners view maintenance as a chore—a weekend lost to cleaning gutters or servicing the HVAC. However, shifting your perspective to view these tasks as investments pays significant dividends. According to real estate experts, every dollar spent on preventative maintenance saves approximately four dollars in future repairs.
          </p>
          
          <ul className="space-y-4 mb-8 list-none pl-0">
            <li className="flex items-start gap-3">
              <CheckCircle2 className="text-primary size-6 shrink-0 mt-0.5" />
              <div>
                <strong>Financial Savings:</strong> Catching a small roof leak early might cost a few hundred dollars to patch. Ignoring it until it rots the framing and ruins interior drywall can cost tens of thousands.
              </div>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle2 className="text-primary size-6 shrink-0 mt-0.5" />
              <div>
                <strong>Enhanced Safety:</strong> Regular checks of electrical systems, smoke detectors, and structural elements protect your family from fire hazards and accidents.
              </div>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle2 className="text-primary size-6 shrink-0 mt-0.5" />
              <div>
                <strong>Energy Efficiency:</strong> Well-maintained appliances, sealed windows, and clean HVAC filters ensure your home runs efficiently, significantly lowering monthly utility bills.
              </div>
            </li>
          </ul>

          <h2 className="text-3xl mt-12 mb-6 flex items-center gap-3">
            <SafeHydrate><Calendar className="text-primary size-8" /></SafeHydrate>
            Your Comprehensive Seasonal Care Checklist
          </h2>
          
          <p className="mb-6">
            The secret to manageable home maintenance is breaking it down by season. Different weather patterns stress different parts of your home. Here is a practical checklist to keep you on track year-round.
          </p>

          <div className="my-8 not-prose">
            <Accordion type="single" collapsible className="w-full" defaultValue="item-1">
              <AccordionItem value="item-1" className="border-border">
                <AccordionTrigger className="text-lg font-semibold hover:text-primary">Spring: Recovery and Preparation</AccordionTrigger>
                <AccordionContent className="text-muted-foreground space-y-2">
                  <p>As the thaw begins, focus on exterior damage from winter and preparing for summer heat.</p>
                  <ul className="list-disc pl-5 space-y-1 mt-2">
                    <li>Inspect the roof for missing or damaged shingles.</li>
                    <li>Clean gutters and downspouts to ensure proper drainage away from the foundation.</li>
                    <li>Schedule a professional HVAC inspection and AC tune-up.</li>
                    <li>Check exterior caulking around windows and doors.</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2" className="border-border">
                <AccordionTrigger className="text-lg font-semibold hover:text-primary">Summer: Exterior and Landscaping</AccordionTrigger>
                <AccordionContent className="text-muted-foreground space-y-2">
                  <p>Take advantage of good weather to tackle outdoor projects.</p>
                  <ul className="list-disc pl-5 space-y-1 mt-2">
                    <li>Inspect and repair deck or patio woodwork; re-seal if necessary.</li>
                    <li>Check exterior siding and paint for peeling or rot.</li>
                    <li>Maintain landscaping to ensure branches are at least 3 feet away from the house.</li>
                    <li>Inspect plumbing for leaks, especially outdoor faucets and sprinkler systems.</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3" className="border-border">
                <AccordionTrigger className="text-lg font-semibold hover:text-primary">Fall: Winterizing and Sealing</AccordionTrigger>
                <AccordionContent className="text-muted-foreground space-y-2">
                  <p>Prepare your home to withstand freezing temperatures and winter storms.</p>
                  <ul className="list-disc pl-5 space-y-1 mt-2">
                    <li>Have the chimney and fireplace professionally inspected and cleaned.</li>
                    <li>Drain and winterize exterior plumbing and irrigation systems.</li>
                    <li>Seal drafts around windows and doors with weatherstripping.</li>
                    <li>Schedule a heating system tune-up before the first freeze.</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-4" className="border-border">
                <AccordionTrigger className="text-lg font-semibold hover:text-primary">Winter: Interior Monitoring</AccordionTrigger>
                <AccordionContent className="text-muted-foreground space-y-2">
                  <p>Focus on interior safety and monitoring exterior stress points.</p>
                  <ul className="list-disc pl-5 space-y-1 mt-2">
                    <li>Test all smoke and carbon monoxide detectors; replace batteries.</li>
                    <li>Monitor the roof for ice dams after heavy snowfalls.</li>
                    <li>Check the attic for proper insulation and ventilation to prevent moisture buildup.</li>
                    <li>Clean refrigerator coils to maintain efficiency.</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          <h2 className="text-3xl mt-12 mb-6 flex items-center gap-3">
            <AlertTriangle className="text-primary size-8" />
            Tackling Common Home Issues Before They Escalate
          </h2>
          
          <p className="mb-6">
            Even with a rigorous checklist, issues will arise. The key is early detection. Water is a home's worst enemy. A small, persistent drip under the kitchen sink can lead to mold growth and cabinet rot within weeks. Regularly inspect under all sinks, around toilets, and near the water heater.
          </p>
          <p className="mb-6">
            Similarly, keep an eye on your foundation. While hairline cracks in concrete are normal settling, cracks wider than a quarter-inch, or those that are growing, require immediate professional assessment. Ignoring structural issues can compromise the entire integrity of the building.
          </p>

          <div className="bg-muted/30 p-6 rounded-xl border border-border my-8 not-prose">
            <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
              <Wrench className="text-primary size-5" />
              Pro Tip: The "One Percent" Rule
            </h3>
            <p className="text-muted-foreground">
              Financial advisors often recommend the "One Percent Rule" for home maintenance budgeting. You should set aside approximately 1% of your home's total purchase price each year for ongoing maintenance and repairs.
            </p>
          </div>

          <h2 className="text-3xl mt-12 mb-6 flex items-center gap-3">
            <Home className="text-primary size-8" />
            Extending Appliance Life
          </h2>
          
          <p className="mb-6">
            Major appliances are expensive to replace. Simple preventative steps can extend their lifespan by years. 
          </p>
          <ul className="list-disc pl-6 space-y-2 mb-8">
            <li><strong>Water Heaters:</strong> Flush your water heater annually to remove sediment buildup, which forces the unit to work harder and can cause premature failure.</li>
            <li><strong>Dryers:</strong> Clean the lint screen after every use, and have the main exhaust vent professionally cleaned annually to prevent fires and improve efficiency.</li>
            <li><strong>Refrigerators:</strong> Vacuum the condenser coils at the back or bottom of the unit twice a year. Dusty coils force the compressor to run hotter and longer.</li>
          </ul>

          <h2 className="text-3xl mt-12 mb-6">Maintaining and Increasing Property Value</h2>
          
          <p className="mb-6">
            Ultimately, consistent home maintenance protects your equity. When it comes time to sell, a well-documented history of maintenance is a powerful selling tool. Buyers are willing to pay a premium for a "turnkey" home where they know the major systems have been cared for. 
          </p>
          <p className="mb-8">
            Conversely, deferred maintenance is the quickest way to lose value. Peeling paint, a sagging roof, or an outdated, poorly maintained HVAC system will result in lowball offers and demanding inspection negotiations. By staying proactive, you ensure your home remains a safe haven and a solid financial asset for years to come.
          </p>

        </article>

        <Separator className="my-16" />

        {}
        <div className="mt-16">
          <div className="flex items-center justify-between mb-8">
            <h3 className="text-2xl font-bold font-default">Related Articles</h3>
            <Link to="/HomePage">
              <Button variant="ghost" className="hidden sm:flex">
                View All Posts
                <ArrowRight className="ml-2 size-4" />
              </Button>
            </Link>
          </div>
          
          <PostsProvider wp_query={{
          post_type: "post",
          per_page: 3,
          order: "desc",
          orderby: "date",
          status: ["publish"]
        }}>
            <RelatedPostsConsumer />
          </PostsProvider>
          
          <div className="mt-8 text-center sm:hidden">
            <Link to="/HomePage" className="w-full">
              <Button variant="outline" className="w-full">
                View All Posts
              </Button>
            </Link>
          </div>
        </div>

      </div>
    </section>;
}